/**
 * Storage rule for inline (data: URL) file parts on user chat messages.
 *
 * An email source file rides the request as a canonical `data:` URL so it
 * never touches the CDN, but a multi-MB payload must not be persisted with
 * the chat (Convex caps a document at 1 MiB, and the import already consumed
 * the bytes). The stored part keeps its name, media type and preview text and
 * swaps the payload for an inert placeholder every reader recognizes.
 */

export const REDACTED_INLINE_ATTACHMENT_URL = 'brew-redacted:inline-attachment'

/** Data URLs at or below this length are small enough to keep verbatim. */
export const INLINE_ATTACHMENT_STORAGE_MAX_CHARS = 16_384

export function isRedactedInlineAttachmentUrl(url?: string): boolean {
  return url === REDACTED_INLINE_ATTACHMENT_URL
}

type FilePartLike = {
  type?: string
  url?: string
  mediaType?: string
}

/** Returns the same object when nothing changes. */
export function compactInlineFilePartForStorage<T extends FilePartLike>(
  part: T
): T {
  if (part.type !== 'file' || typeof part.url !== 'string') {
    return part
  }
  if (!part.url.startsWith('data:')) {
    return part
  }
  if ((part.mediaType ?? '').toLowerCase().startsWith('image/')) {
    return part
  }
  if (part.url.length <= INLINE_ATTACHMENT_STORAGE_MAX_CHARS) {
    return part
  }
  return { ...part, url: REDACTED_INLINE_ATTACHMENT_URL }
}
